/**
 * Menu for the app using only template/MUI components.
 * Route config and role-based visibility; no legacy components.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import EvStationRoundedIcon from '@mui/icons-material/EvStationRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import BatteryChargingFullRoundedIcon from '@mui/icons-material/BatteryChargingFullRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';
import QuizRoundedIcon from '@mui/icons-material/QuizRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import HelpCenterRoundedIcon from '@mui/icons-material/HelpCenterRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import DesignServicesRoundedIcon from '@mui/icons-material/DesignServicesRounded';
import BuildCircleRoundedIcon from '@mui/icons-material/BuildCircleRounded';
import EngineeringRoundedIcon from '@mui/icons-material/EngineeringRounded';

const ALL_ITEMS = [
  { path: '/', labelKey: 'Dashboard', icon: <HomeRoundedIcon />, roles: ['ADMIN', 'MERCHANT', 'SUPER_ADMIN', 'OPERATOR'] },
  { path: '/users', labelKey: 'Users', icon: <PeopleRoundedIcon />, roles: ['SUPER_ADMIN', 'OPERATOR'] },
  { path: '/station', labelKey: 'ChargeStation', icon: <EvStationRoundedIcon />, roles: ['ADMIN', 'MERCHANT', 'SUPER_ADMIN', 'OPERATOR'] },
  { path: '/rents', labelKey: 'Rents', icon: <ReceiptLongRoundedIcon />, roles: ['ADMIN', 'MERCHANT', 'SUPER_ADMIN', 'OPERATOR'] },
  { path: '/powerBank', labelKey: 'Powerbanks', icon: <BatteryChargingFullRoundedIcon />, roles: ['ADMIN', 'MERCHANT', 'SUPER_ADMIN', 'OPERATOR'] },
  { path: '/finance', labelKey: 'Finance', icon: <AccountBalanceWalletRoundedIcon />, roles: ['ADMIN', 'MERCHANT', 'SUPER_ADMIN'] },
  { path: '/service/tickets', labelKey: 'ServiceTickets', icon: <BuildCircleRoundedIcon />, roles: ['ADMIN', 'SUPER_ADMIN', 'OPERATOR', 'TECHNICIAN'] },
  { path: '/service/technicians', labelKey: 'ServiceTechnicians', icon: <EngineeringRoundedIcon />, roles: ['ADMIN', 'SUPER_ADMIN', 'OPERATOR'] },
  { path: '/tariff', labelKey: 'Tariffs', icon: <PercentRoundedIcon />, roles: ['SUPER_ADMIN'] },
  { path: '/partners', labelKey: 'Merchants', icon: <PeopleRoundedIcon />, roles: ['SUPER_ADMIN'], hidden: true },
  { path: '/Q&A', labelKey: 'QA', icon: <QuizRoundedIcon />, roles: ['SUPER_ADMIN'], hidden: true },
  { path: '/documents', labelKey: 'Documents', icon: <DescriptionRoundedIcon />, roles: ['SUPER_ADMIN'], hidden: true },
  { path: '/support', labelKey: 'Support', icon: <HelpCenterRoundedIcon />, roles: ['SUPER_ADMIN'], hidden: true },
  { path: '/merchants', labelKey: 'Merchants', icon: <AdminPanelSettingsRoundedIcon />, roles: ['ADMIN', 'SUPER_ADMIN'] },
  { path: '/design-system', labelKey: 'DesignSystem', icon: <DesignServicesRoundedIcon />, roles: ['SUPER_ADMIN'] },
  { path: '/license_agreement', labelKey: 'LicenseAgreement', icon: <DescriptionRoundedIcon />, roles: ['ADMIN', 'MERCHANT', 'SUPER_ADMIN'], hidden: true },
];

function getRole() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem('role') || '' : '';
  } catch {
    return '';
  }
}

function isSelected(path, hash) {
  const h = hash || (typeof window !== 'undefined' ? window.location.hash : '') || '#/';
  if (path === '/') return h === '#/' || h === '#';
  return h === `#${path}` || h.startsWith(`#${path}?`);
}

export default function AppMenuContent({ onNavigate }) {
  const { t } = useTranslation();
  const role = getRole();
  const items = ALL_ITEMS.filter((item) => !item.hidden && item.roles.includes(role));
  const hash = typeof window !== 'undefined' ? window.location.hash : '';

  return (
    <Stack sx={{ flexGrow: 1, p: 1, justifyContent: 'space-between' }}>
      <List dense>
        {items.map((item) => (
          <ListItem key={item.path} disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              component="a"
              href={`#${item.path}`}
              selected={isSelected(item.path, hash)}
              onClick={onNavigate}
              sx={{
                borderRadius: 2,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 38 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={t(`menu.${item.labelKey}`)} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Stack>
  );
}
